import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Plus, Edit2, Trash2, X, Eye, Image,
  PanelTop, Search, SortAsc, SortDesc, Filter, ChevronDown,
} from 'lucide-react';
import Pagination from '../components/Pagination';

const API = process.env.REACT_APP_API_URL || '';

const defaultForm = {
  name: '', description: '', mode: 'form',
  backgroundColor: '#6B21A8', gradientColor: '#9333EA', accentColor: '#ffffff',
  headerText: 'Dear Doctor,',
  bodyText: 'Treating epilepsy is more than a medical practice - it is an art of restoring quality of life.',
  highlightText: 'International Epilepsy Day / Purple Day 2026',
  footerText: 'Thank you for your unwavering commitment to excellence.',
  companyName: 'Hilton Pharma', productName: 'LERACE (Levetiracetam)',
  showButterfly: true,
  photoX: 50, photoY: 18, photoSize: 22,
  nameX: 50, nameY: 38, nameColor: '#ffffff', nameFontSize: 22,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Detect whether a saved (server-side) file URL is a video.
 * Only used for already-uploaded files — NOT for new blob URLs.
 */
const isSavedFileVideo = (url) =>
  url ? /\.(mp4|mov|avi|webm)$/i.test(url) : false;

// ─── Butterfly SVG ───────────────────────────────────────────────────────────

function Butterfly({ size = 70 }) {
  return (
    <svg width={size} height={size * 0.8} viewBox="0 0 200 160">
      <ellipse cx="60" cy="60" rx="55" ry="45" fill="#C084FC" opacity="0.9" transform="rotate(-20,60,60)" />
      <ellipse cx="140" cy="60" rx="55" ry="45" fill="#A855F7" opacity="0.9" transform="rotate(20,140,60)" />
      <ellipse cx="55" cy="100" rx="38" ry="28" fill="#9333EA" opacity="0.85" transform="rotate(20,55,100)" />
      <ellipse cx="145" cy="100" rx="38" ry="28" fill="#7C3AED" opacity="0.85" transform="rotate(-20,145,100)" />
      <path d="M100,20 Q103,80 100,140 Q97,80 100,20" fill="#4C1D95" />
    </svg>
  );
}

// ─── Form Poster Preview ──────────────────────────────────────────────────────

function FormPosterPreview({ template }) {
  const bg1 = template.backgroundColor || '#6B21A8';
  const bg2 = template.gradientColor || '#9333EA';
  return (
    <div style={{
      width: '100%', maxWidth: 280, margin: '0 auto',
      background: `linear-gradient(160deg, ${bg1} 0%, ${bg2} 100%)`,
      borderRadius: 16, overflow: 'hidden', fontFamily: 'DM Sans, sans-serif',
    }}>
      <div style={{ padding: '20px 18px 0', textAlign: 'center', color: template.accentColor || '#fff' }}>
        <div style={{
          width: 60, height: 60, borderRadius: '50%',
          background: 'rgba(255,255,255,0.2)', border: '3px solid rgba(255,255,255,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 10px', fontSize: '1.5rem',
        }}>👨‍⚕️</div>
        <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 10 }}>Sample Name</div>
        <div style={{ fontSize: '0.65rem', lineHeight: 1.6 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{template.headerText}</div>
          <div style={{ opacity: 0.9, marginBottom: 6 }}>{(template.bodyText || '').substring(0, 70)}...</div>
          {template.highlightText && (
            <div style={{
              fontWeight: 700, background: 'rgba(255,255,255,0.12)', borderRadius: 6,
              padding: '4px 8px', margin: '6px 0', display: 'inline-block', fontSize: '0.6rem',
            }}>{template.highlightText}</div>
          )}
          <div style={{ fontWeight: 700, marginTop: 6, fontSize: '0.62rem' }}>
            {(template.footerText || '').substring(0, 50)}
          </div>
        </div>
      </div>
      <div style={{
        marginTop: 14, background: 'rgba(255,255,255,0.95)',
        padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        {template.showButterfly && <Butterfly size={50} />}
        <div style={{ fontSize: '0.6rem', color: '#4C1D95', fontWeight: 700 }}>{template.productName}</div>
        <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#1F2937' }}>{template.companyName}</div>
      </div>
    </div>
  );
}

// ─── Image / Video Poster Preview ────────────────────────────────────────────

/**
 * Props:
 *   template    – template object from state / API
 *   bgPreview   – blob URL of a newly selected local file (null if none)
 *   bgIsVideo   – boolean, true when the newly selected local file is a video
 *                 (derived from File.type, NOT from the blob URL string)
 */
function ImagePosterPreview({ template, bgPreview = null, bgIsVideo = false, autoplayAndMute = false }) {
  const posterW = 280;
  const posterH = 420;

  // Prefer the fresh local blob, fall back to the saved server path.
  const src = bgPreview || (template.backgroundImage ? `${API}${template.backgroundImage}` : null);

  // FIX: For a newly chosen local file we use the reliable `bgIsVideo` flag
  //      (derived from File.type). For a saved server file we check the extension.
  //      We NEVER test blob URLs against file extensions — blob URLs have no extension.
  const isVideo = bgPreview ? bgIsVideo : isSavedFileVideo(src);

  const photoSize = Math.round((template.photoSize / 100) * posterW);
  const photoLeft = Math.round((template.photoX / 100) * posterW) - photoSize / 2;
  const photoTop = Math.round((template.photoY / 100) * posterH) - photoSize / 2;
  const nameLeft = Math.round((template.nameX / 100) * posterW);
  const nameTop = Math.round((template.nameY / 100) * posterH);

  return (
    <div style={{
      width: posterW, height: posterH, margin: '0 auto', position: 'relative',
      borderRadius: 16, overflow: 'hidden',
      background: src ? 'transparent' : '#e5e7eb',
      border: '1px solid var(--gray-200)',
    }}>
      {src ? (
        isVideo ? (
          autoplayAndMute ?
            <video
              key={src}                   // force remount on source change
              src={src}
              autoPlay loop muted playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            /> :
            <video key={src} src={src} autoPlay loop controls playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}>
              Your browser does not support the video tag.
            </video>
            // <video key={src} src={src} controls style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}>
            //   Your browser does not support the video tag.
            // </video>
        ) : (
          <img
            key={src}                   // force remount on source change
            src={src}
            alt="bg"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )
      ) : (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', height: '100%',
          color: 'var(--gray-400)', fontSize: '0.8rem', gap: 8,
        }}>
          <Image size={32} />
          <span>Upload background image/video</span>
          <span style={{ fontSize: '0.7rem' }}>Recommended: 800×1200px</span>
        </div>
      )}

      {src && (
        <>
          <div style={{
            position: 'absolute', left: photoLeft, top: photoTop,
            width: photoSize, height: photoSize, borderRadius: '50%',
            border: '3px solid rgba(255,255,255,0.8)', background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: Math.round(photoSize * 0.4),
          }}>👨‍⚕️</div>

          <div style={{
            position: 'absolute', left: nameLeft, top: nameTop,
            transform: 'translateX(-50%)',
            color: template.nameColor || '#fff',
            fontSize: template.nameFontSize ? template.nameFontSize * (posterW / 800) : 14,
            fontWeight: 700,
            textShadow: '0 2px 8px rgba(0,0,0,0.6)',
            textAlign: 'center', whiteSpace: 'nowrap',
            fontFamily: 'DM Sans, sans-serif',
          }}>Sample Name</div>
        </>
      )}
    </div>
  );
}

// ─── Template Form Modal ──────────────────────────────────────────────────────

function TemplateForm({ template, onSave, onClose }) {
  const [form, setForm] = useState(template ? { ...defaultForm, ...template } : { ...defaultForm });
  const [loading, setLoading] = useState(false);

  // FIX: Track blob URL and MIME-based video flag separately.
  const [bgPreview, setBgPreview] = useState(null);
  const [bgIsVideo, setBgIsVideo] = useState(false);

  const companyLogoRef = useRef();
  const productLogoRef = useRef();
  const backgroundImageRef = useRef();

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Track latest blob URL in a ref so the unmount cleanup always revokes the correct one.
  const bgPreviewRef = useRef(null);

  const handleBgFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Revoke previous blob to avoid memory leaks.
    if (bgPreviewRef.current) URL.revokeObjectURL(bgPreviewRef.current);

    const url = URL.createObjectURL(file);
    bgPreviewRef.current = url;
    setBgPreview(url);
    setBgIsVideo(file.type.startsWith('video/')); // ← MIME type, 100% reliable
  };

  // Cleanup blob URL on unmount — uses ref so it always has the latest URL.
  useEffect(() => {
    return () => {
      if (bgPreviewRef.current) URL.revokeObjectURL(bgPreviewRef.current);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error('Template name required');
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (typeof v !== 'object') fd.append(k, v);
      });
      if (companyLogoRef.current?.files[0])
        fd.append('companyLogo', companyLogoRef.current.files[0]);
      if (productLogoRef.current?.files[0])
        fd.append('productLogo', productLogoRef.current.files[0]);
      if (backgroundImageRef.current?.files[0])
        fd.append('backgroundImage', backgroundImageRef.current.files[0]);

      if (template?._id) {
        await axios.put(`/api/templates/${template._id}`, fd);
        toast.success('Template updated!');
      } else {
        await axios.post('/api/templates', fd);
        toast.success('Template created!');
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  // Thumbnail shown inside the upload area after a file is chosen.
  const renderUploadThumbnail = () => {
    if (!bgPreview) return null;
    return bgIsVideo ? (
      // <video
      //   src={bgPreview}
      //   autoPlay loop muted playsInline
      //   style={{ width: 44, height: 66, objectFit: 'cover', borderRadius: 6, border: '2px solid var(--purple-400)' }}
      // />
      <video style={{ width: 44, height: 66, objectFit: 'cover', borderRadius: 6, border: '2px solid var(--purple-400)' }} controls>
        <source src={bgPreview} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    ) : (
      <img
        src={bgPreview}
        alt="preview"
        style={{ width: 44, height: 66, objectFit: 'cover', borderRadius: 6, border: '2px solid var(--purple-400)' }}
      />
    );
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 820 }}>
        <div className="modal-header">
          <h2>{template ? 'Edit Template' : 'Create Poster Template'}</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* ── Mode selector ── */}
          <div className="form-group">
            <label className="form-label">Template Mode</label>
            <div style={{ display: 'flex', gap: 10 }}>
              {[
                ['form', 'Built-in Designer', 'Customize colors, text, logos', PanelTop],
                ['image', 'Custom Background Image', 'Upload your own poster design', Image],
              ].map(([mode, title, desc, Icon]) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => set('mode', mode)}
                  style={{
                    flex: 1, padding: '10px 14px', borderRadius: 10,
                    border: `2px solid ${form.mode === mode ? 'var(--purple-800)' : 'var(--gray-200)'}`,
                    background: form.mode === mode ? 'var(--purple-100)' : 'white',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                    fontFamily: 'DM Sans, sans-serif', fontWeight: 600,
                    color: form.mode === mode ? 'var(--purple-800)' : 'var(--gray-600)',
                  }}
                >
                  <Icon size={16} />{title}
                  <span style={{ fontSize: '0.72rem', fontWeight: 400, marginLeft: 4 }}>{desc}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid-2" style={{ gap: 24, alignItems: 'start' }}>
            {/* ── Left column ── */}
            <div>
              <div className="form-group">
                <label className="form-label">Template Name *</label>
                <input
                  className="form-input"
                  value={form.name}
                  onChange={e => set('name', e.target.value)}
                  placeholder="e.g. Epilepsy Day 2026"
                  required
                />
              </div>

              {form.mode === 'form' ? (
                <>
                  <div className="grid-2">
                    {[['backgroundColor', 'Background Color'], ['gradientColor', 'Gradient Color']].map(([k, l]) => (
                      <div key={k} className="form-group">
                        <label className="form-label">{l}</label>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <input type="color" value={form[k]} onChange={e => set(k, e.target.value)}
                            style={{ width: 40, height: 36, border: 'none', cursor: 'pointer', borderRadius: 8 }} />
                          <input className="form-input" value={form[k]} onChange={e => set(k, e.target.value)} style={{ flex: 1 }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Header Text</label>
                    <input className="form-input" value={form.headerText} onChange={e => set('headerText', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Body Text</label>
                    <textarea className="form-input" value={form.bodyText} onChange={e => set('bodyText', e.target.value)} rows={3} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Highlight / Campaign Name</label>
                    <input className="form-input" value={form.highlightText} onChange={e => set('highlightText', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Footer Text</label>
                    <textarea className="form-input" value={form.footerText} onChange={e => set('footerText', e.target.value)} rows={2} />
                  </div>
                  <div className="grid-2">
                    <div className="form-group">
                      <label className="form-label">Company Name</label>
                      <input className="form-input" value={form.companyName} onChange={e => set('companyName', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Product Name</label>
                      <input className="form-input" value={form.productName} onChange={e => set('productName', e.target.value)} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Company Logo</label>
                    <input type="file" ref={companyLogoRef} accept="image/*" className="form-input" style={{ padding: '7px 12px' }} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Product Logo</label>
                    <input type="file" ref={productLogoRef} accept="image/*" className="form-input" style={{ padding: '7px 12px' }} />
                  </div>
                </>
              ) : (
                <>
                  {/* ── Background upload ── */}
                  <div className="form-group">
                    <label className="form-label">Background Poster</label>
                    <div style={{
                      background: 'var(--purple-100)', borderRadius: 10, padding: 12,
                      marginBottom: 10, fontSize: '0.8rem', color: 'var(--purple-800)', lineHeight: 1.6,
                    }}>
                      <strong>Recommended:</strong> 800×1200px (portrait) - Images or Videos supported
                    </div>

                    <div
                      className="upload-area"
                      onClick={() => backgroundImageRef.current?.click()}
                      style={{ padding: 16, textAlign: 'center', cursor: 'pointer' }}
                    >
                      {/* {bgPreview ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center' }}>
                          {renderUploadThumbnail()}
                          <div style={{ textAlign: 'left' }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--purple-800)' }}>File selected</div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>Click to change</div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                            style={{ margin: '0 auto 6px', display: 'block' }}>
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"
                              stroke="var(--purple-600)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--purple-700)' }}>
                            Click to upload poster
                          </div>
                          <div style={{ fontSize: '0.71rem', color: 'var(--gray-400)', marginTop: 3 }}>
                            PNG, JPG, JPEG, GIF, SVG · MP4, MOV, AVI, WEBM · Max 50MB
                          </div>
                        </>
                      )} */}
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                        style={{ margin: '0 auto 6px', display: 'block' }}>
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"
                          stroke="var(--purple-600)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--purple-700)' }}>
                        Click to upload poster
                      </div>
                      <div style={{ fontSize: '0.71rem', color: 'var(--gray-400)', marginTop: 3 }}>
                        PNG, JPG, JPEG, GIF, SVG · MP4, MOV, AVI, WEBM · Max 50MB
                      </div>
                    </div>

                    {/* FIX: onChange uses handleBgFileChange which sets bgIsVideo from File.type */}
                    <input
                      type="file"
                      ref={backgroundImageRef}
                      accept="image/png,image/jpg,image/jpeg,image/gif,image/svg+xml,video/mp4,video/quicktime,video/x-msvideo,video/webm"
                      style={{ display: 'none' }}
                      onChange={handleBgFileChange}
                    />
                  </div>

                  {/* ── Position controls ── */}
                  <div style={{
                    background: 'var(--gray-50)', borderRadius: 10,
                    padding: 16, border: '1px solid var(--gray-200)',
                  }}>
                    <div style={{
                      fontWeight: 600, fontSize: '0.78rem', color: 'var(--gray-600)',
                      marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px',
                    }}>Doctor Photo Position</div>

                    <div className="grid-2">
                      <div className="form-group">
                        <label className="form-label">Left ({form.photoX}%)</label>
                        <input type="range" min="10" max="90" value={form.photoX}
                          onChange={e => set('photoX', +e.target.value)}
                          style={{ width: '100%', accentColor: 'var(--purple-800)' }} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Top ({form.photoY}%)</label>
                        <input type="range" min="5" max="70" value={form.photoY}
                          onChange={e => set('photoY', +e.target.value)}
                          style={{ width: '100%', accentColor: 'var(--purple-800)' }} />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Photo Size ({form.photoSize}%)</label>
                      <input type="range" min="10" max="50" value={form.photoSize}
                        onChange={e => set('photoSize', +e.target.value)}
                        style={{ width: '100%', accentColor: 'var(--purple-800)' }} />
                    </div>

                    <div style={{
                      fontWeight: 600, fontSize: '0.78rem', color: 'var(--gray-600)',
                      margin: '10px 0 8px', textTransform: 'uppercase', letterSpacing: '0.5px',
                    }}>Doctor Name Position</div>

                    <div className="grid-2">
                      <div className="form-group">
                        <label className="form-label">Left ({form.nameX}%)</label>
                        <input type="range" min="10" max="90" value={form.nameX}
                          onChange={e => set('nameX', +e.target.value)}
                          style={{ width: '100%', accentColor: 'var(--purple-800)' }} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Top ({form.nameY}%)</label>
                        <input type="range" min="5" max="90" value={form.nameY}
                          onChange={e => set('nameY', +e.target.value)}
                          style={{ width: '100%', accentColor: 'var(--purple-800)' }} />
                      </div>
                    </div>

                    <div className="grid-2">
                      <div className="form-group">
                        <label className="form-label">Font Size ({form.nameFontSize}px)</label>
                        <input type="range" min="12" max="60" value={form.nameFontSize}
                          onChange={e => set('nameFontSize', +e.target.value)}
                          style={{ width: '100%', accentColor: 'var(--purple-800)' }} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Name Color</label>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <input type="color" value={form.nameColor}
                            onChange={e => set('nameColor', e.target.value)}
                            style={{ width: 40, height: 36, border: 'none', cursor: 'pointer', borderRadius: 8 }} />
                          <input className="form-input" value={form.nameColor}
                            onChange={e => set('nameColor', e.target.value)} style={{ flex: 1 }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* ── Right column: live preview ── */}
            <div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12,
                fontWeight: 600, fontSize: '0.78rem', color: 'var(--gray-600)',
                textTransform: 'uppercase', letterSpacing: '0.5px',
              }}>
                <Eye size={14} /> Live Preview
              </div>

              {form.mode === 'form' ? (
                <FormPosterPreview template={form} />
              ) : (
                <ImagePosterPreview template={form} bgPreview={bgPreview} bgIsVideo={bgIsVideo} />
              )}
            </div>
          </div>

          {/* ── Footer actions ── */}
          <div style={{
            display: 'flex', gap: 10, justifyContent: 'flex-end',
            marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--gray-100)',
          }}>
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <><div className="spinner" />&nbsp;Saving...</> : 'Save Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Templates Page ──────────────────────────────────────────────────────

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [mode, setMode] = useState('');
  const [sort, setSort] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);

  const searchTimer = useRef(null);

  const fetchTemplates = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const p = { page, limit, search, sort, order, mode, ...params };
      const qs = new URLSearchParams();
      Object.entries(p).forEach(([k, v]) => { if (v !== '' && v != null) qs.set(k, v); });
      const res = await axios.get(`/api/templates?${qs.toString()}`);
      setTemplates(res.data.data);
      setPagination(res.data.pagination);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, sort, order, mode]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleSearch = (val) => {
    setSearch(val);
    setPage(1);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchTemplates({ search: val, page: 1 }), 400);
  };

  const handleSort = (field, newOrder) => {
    setSort(field);
    setOrder(newOrder);
    setPage(1);
    fetchTemplates({ sort: field, order: newOrder, page: 1 });
  };

  const handleMode = (val) => {
    setMode(val);
    setPage(1);
    fetchTemplates({ mode: val, page: 1 });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`/api/templates/${id}`);
      toast.success('Deleted');
      fetchTemplates();
    } catch {
      toast.error('Delete failed');
    }
  };

  const activeFilters = [search, mode].filter(Boolean).length;

  return (
    <div>
      {/* ── Page header ── */}
      <div className="page-header">
        <div>
          <h1>Poster Templates</h1>
          <p>{pagination.total} templates total</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditing(null); setShowForm(true); }}>
          <Plus size={16} /> New Template
        </button>
      </div>

      {/* ── Toolbar ── */}
      <div className="card" style={{ marginBottom: 20, padding: '14px 18px' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>

          {/* Search */}
          <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 160 }}>
            <Search size={15} style={{
              position: 'absolute', left: 11, top: '50%',
              transform: 'translateY(-50%)', color: 'var(--gray-400)',
            }} />
            <input
              className="form-input"
              placeholder="Search templates..."
              value={search}
              onChange={e => handleSearch(e.target.value)}
              style={{ paddingLeft: 34, margin: 0 }}
            />
          </div>

          {/* Mode filter */}
          <div style={{ position: 'relative' }}>
            <Filter size={13} style={{
              position: 'absolute', left: 10, top: '50%',
              transform: 'translateY(-50%)',
              color: mode ? 'var(--purple-800)' : 'var(--gray-400)', pointerEvents: 'none',
            }} />
            <select
              value={mode}
              onChange={e => handleMode(e.target.value)}
              style={{
                paddingLeft: 28, paddingRight: 28, height: 38, borderRadius: 10,
                border: `1.5px solid ${mode ? 'var(--purple-400)' : 'var(--gray-200)'}`,
                fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', cursor: 'pointer',
                outline: 'none', color: mode ? 'var(--purple-800)' : 'var(--gray-600)',
                background: mode ? 'var(--purple-100)' : 'white', appearance: 'none',
              }}
            >
              <option value="">All Types</option>
              <option value="form">🎨 Built-in Designer</option>
              <option value="image">🖼 Custom Image</option>
            </select>
            <ChevronDown size={13} style={{
              position: 'absolute', right: 10, top: '50%',
              transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--gray-400)',
            }} />
          </div>

          {/* Sort buttons */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>Sort:</span>
            {[['name', 'Name'], ['createdAt', 'Date']].map(([f, l]) => (
              <button
                key={f}
                onClick={() => handleSort(f, sort === f && order === 'desc' ? 'asc' : 'desc')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '5px 10px', borderRadius: 7,
                  border: `1.5px solid ${sort === f ? 'var(--purple-400)' : 'var(--gray-200)'}`,
                  background: sort === f ? 'var(--purple-100)' : 'white',
                  color: sort === f ? 'var(--purple-800)' : 'var(--gray-600)',
                  fontSize: '0.78rem', fontWeight: sort === f ? 600 : 400,
                  cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                }}
              >
                {l}
                {sort === f
                  ? (order === 'asc' ? <SortAsc size={12} /> : <SortDesc size={12} />)
                  : <SortAsc size={12} style={{ opacity: 0.3 }} />}
              </button>
            ))}
          </div>

          {/* Clear filters */}
          {activeFilters > 0 && (
            <button
              className="btn btn-sm btn-outline"
              onClick={() => {
                setSearch(''); setMode('');
                setSort('createdAt'); setOrder('desc'); setPage(1);
                fetchTemplates({ search: '', mode: '', sort: 'createdAt', order: 'desc', page: 1 });
              }}
            >
              <X size={13} /> Clear ({activeFilters})
            </button>
          )}
        </div>
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner" style={{
            width: 36, height: 36,
            borderColor: 'rgba(107,33,168,0.2)', borderTopColor: 'var(--purple-800)',
          }} />
        </div>
      ) : (
        <>
          <Pagination
            page={pagination.page}
            limit={pagination.limit}
            total={pagination.total}
            onPageChange={p => { setPage(p); fetchTemplates({ page: p }); }}
            onLimitChange={l => { setLimit(l); setPage(1); fetchTemplates({ limit: l, page: 1 }); }}
          />

          {templates.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 60 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🎨</div>
              <h3 style={{ marginBottom: 8 }}>No templates found</h3>
              <p style={{ color: 'var(--gray-400)', marginBottom: 20 }}>
                {search || mode ? 'Try adjusting your filters' : 'Create your first poster template'}
              </p>
              {!search && !mode && (
                <button className="btn btn-primary" onClick={() => setShowForm(true)}>
                  <Plus size={16} /> Create Template
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
              {templates.map(tmpl => (
                <div className="card" key={tmpl._id} style={{ padding: 0, overflow: 'hidden' }}>
                  <div
                    style={{ padding: 16, cursor: 'pointer', background: 'var(--gray-50)' }}
                    onClick={() => setPreviewing(tmpl)}
                  >
                    {/* Saved templates: derive isVideo from server URL extension (no blob involved) */}
                    {tmpl.mode === 'image' && tmpl.backgroundImage
                      ? <ImagePosterPreview template={tmpl} autoplayAndMute />
                      : <FormPosterPreview template={tmpl} />}
                  </div>
                  <div style={{
                    padding: '10px 14px', borderTop: '1px solid var(--gray-100)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{tmpl.name}</div>
                      <span
                        className={`badge ${tmpl.mode === 'image' ? 'badge-purple' : 'badge-green'}`}
                        style={{ marginTop: 3 }}
                      >
                        {tmpl.mode === 'image' ? '🖼 Image' : '🎨 Designer'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => { setEditing(tmpl); setShowForm(true); }}
                      ><Edit2 size={12} /></button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(tmpl._id)}
                      ><Trash2 size={12} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Modals ── */}
      {showForm && (
        <TemplateForm
          template={editing}
          onSave={() => { setShowForm(false); fetchTemplates(); }}
          onClose={() => setShowForm(false)}
        />
      )}

      {previewing && (
        <div className="modal-overlay" onClick={() => setPreviewing(null)}>
          <div className="modal" style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <h2>{previewing.name}</h2>
              <button className="modal-close" onClick={() => setPreviewing(null)}><X size={20} /></button>
            </div>
            {previewing.mode === 'image' && previewing.backgroundImage
              ? <ImagePosterPreview template={previewing} autoplayAndMute />
              : <FormPosterPreview template={previewing} />}
          </div>
        </div>
      )}
    </div>
  );
}